<script lang="ts" setup>
import UserAPI from '~/api/user';

const props = defineProps<{
  visible: boolean;
  phone: string;
}>();
const visible = toRef(props, 'visible');
const emit = defineEmits<{
  (event: 'update:visible', value: boolean): void;
  (event: 'completed'): void;
}>();

const { t } = useI18n();
const modalStore = useModalStore();

const [modalVisible, onToggleVisible] = useExternalModalState(visible, (v) =>
  emit('update:visible', v),
);

const isError = ref(false);
const isLoading = ref(false);

const onSubmit = (code: string) => {
  isError.value = false;

  if (!isLoading.value) {
    isLoading.value = true;
    UserAPI.checkPhoneCode({ body: { code } })
      ?.then((data) => {
        if (data.codeValid) {
          onToggleVisible(false);
          emit('completed');
          modalStore.showSuccess({
            title: t('message.success'),
            action: t('message.action'),
          });
        } else {
          isError.value = true;
        }
      })
      .catch(() => {
        isError.value = true;
      })
      .finally(() => {
        isLoading.value = false;
      });
  }
};
</script>

<template>
  <BaseModal
    :visible="modalVisible"
    @update:visible="onToggleVisible"
    fullscreen
    :aria-title="t('title')"
    :aria-describedby="t('description')"
    container-class="profilePhoneVerificationModal"
  >
    <h4 class="profilePhoneVerificationModal__title">{{ t('label') }}</h4>

    <ProfilePhoneCodeForm
      @complete="onSubmit"
      @input="isError = false"
      @change-phone="onToggleVisible(false)"
      :repeat-code="() => UserAPI.confirmPhone()"
      :call-attempts="0"
      :phone="phone"
      :failed="isError"
      :readonly="isLoading"
    />
  </BaseModal>
</template>

<i18n>
ru:
  title: Подтвердить номер телефона
  description: Подтверждение номера телефона через СМС код
  label: Введите код
  message:
    success: Номер телефона успешно подтвержден
    action: Вернуться

en:
  title: Confirm phone number
  description: Confirm phone number via SMS code
  label: Enter code
  message:
    success: Phone number successfully confirmed
    action: Back
</i18n>

<style lang="scss">
.profilePhoneVerificationModal {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-bottom: 80px;

  &__title {
    font-size: 34px;
    line-height: 40px;
    text-align: center;
    letter-spacing: 0.2px;
    color: #121212;
    margin-top: 0;
    margin-bottom: 32px;
  }
}
</style>
